import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { Button } from "~/components/Button";
import Link from "~/components/Link";
import { Dropdown } from "~/components/Dropdown";
import { PageLoading } from "~/components/PageLoading";
import { WithStudentNav } from "~/components/WithStudentNav";
import { trpc } from "~/utils/trpc.client";
import { StudentBiographicalInformation } from "~/app/(new)/[teamId]/(with-student-nav)/students/[studentId]/StudentBiographicalInformation";
import { StudentGoal } from "~/app/(new)/[teamId]/(with-student-nav)/students/[studentId]/StudentGoal";
import { StudentReportCreatorButton } from "~/app/(new)/[teamId]/(with-student-nav)/students/[studentId]/StudentReportCreatorButton";
import { StudentFileList } from "~/app/(new)/[teamId]/(with-student-nav)/students/[studentId]/StudentFileList";

/**
 * Detailed view of a student: biographical details, goals, reports, and files.
 */
export const Route = createFileRoute("/_authed/$teamId/students/$studentId/")({
  component: StudentPage,
});

function StudentPage() {
  const { teamId, studentId } = Route.useParams();

  const studentQuery = trpc.students.getStudent.useQuery({ id: studentId });
  const teamQuery = trpc.team.getTeamById.useQuery({ id: teamId });
  const reportsQuery = trpc.reports.getReportsByStudent.useQuery({ studentId });
  const filesQuery = trpc.files.getAllFilesByStudent.useQuery({ studentId });
  const reportTypesQuery = trpc.reportTypes.getReportTypes.useQuery({ teamId });
  const goalsQuery = trpc.goals.getGoals.useQuery({ studentId });
  const measurementsQuery =
    trpc.goals.getGoalMeasurementsForStudent.useQuery({ studentId });
  const milestonesQuery =
    trpc.goals.getGoalMilestonesForStudent.useQuery({ studentId });
  const featureFlagsQuery = trpc.team.getFeatureFlags.useQuery({ teamId });

  const logStudentView = trpc.students.logStudentView.useMutation();
  useEffect(() => {
    logStudentView.mutate({ studentId });
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  if (
    !studentQuery.data ||
    !teamQuery.data ||
    !reportsQuery.data ||
    !filesQuery.data ||
    !reportTypesQuery.data ||
    !goalsQuery.data ||
    !measurementsQuery.data ||
    !milestonesQuery.data ||
    !featureFlagsQuery.data
  ) {
    return (
      <WithStudentNav>
        <div className="m-auto flex w-full max-w-[800px] flex-col gap-12 p-4">
          <PageLoading />
        </div>
      </WithStudentNav>
    );
  }

  const student = studentQuery.data;
  const team = teamQuery.data;
  const reports = reportsQuery.data;
  const files = filesQuery.data;
  const reportTypes = reportTypesQuery.data;
  const goals = goalsQuery.data;
  const measurements = measurementsQuery.data;
  const milestones = milestonesQuery.data;
  const featureFlags = featureFlagsQuery.data;

  return (
    <WithStudentNav>
      <div className="m-auto flex w-full max-w-[800px] flex-col gap-12 p-4">
        <div className="flex flex-row w-full -mb-8">
          <div className="breadcrumbs text-sm overflow-visible">
            <ul>
              <li>
                <Link href={`/${teamId}`}>Team Home</Link>
              </li>
              <li>
                <span>{student.name}</span>
              </li>
            </ul>
          </div>
        </div>
        {/* Biographical Information */}
        <div className="card bg-base-100  shadow">
          <div className="card-body">
            <StudentBiographicalInformation student={student} team={team} />
          </div>
        </div>
        {/* Goals */}
        {featureFlags.find((f) => f.id === "goals")?.enabled && (
          <div className="flex flex-col gap-4">
            <div className="flex justify-between">
              <Link href={`/${teamId}/students/${studentId}/goals`}>
                <h2 className="text-xl font-bold">Goals</h2>
              </Link>
              <div className="flex gap-4 flex-row-reverse">
                {goals.length > 0 && (
                  <Link
                    href={`/${teamId}/students/${studentId}/goals/track`}
                    className="btn btn-primary btn-sm"
                  >
                    Track Progress
                  </Link>
                )}
                {goals.length > 0 && (
                  <Link
                    href={`/${teamId}/students/${studentId}/goals?addGoal=1`}
                    className="btn btn-sm btn-secondary"
                  >
                    Add Goals
                  </Link>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {goals
                .filter((g) => g.parentGoalId == null)
                .map((goal) => (
                  <Link
                    key={goal.id}
                    href={`/${teamId}/students/${studentId}/goals`}
                  >
                    <div className="card bg-base-100 shadow">
                      <div className="card-body">
                        <StudentGoal
                          goal={goal}
                          // don't show any subgoals on this page
                          goals={[]}
                          measurements={measurements}
                          milestones={milestones}
                        />
                      </div>
                    </div>
                  </Link>
                ))}
              {goals.length === 0 && (
                <div className="flex flex-col gap-4 card bg-base-100 shadow">
                  <div className="card-body">
                    <div className="w-full text-center italic opacity-70 p-4">
                      No goals have been created yet.
                    </div>
                    <div className="flex gap-4 w-full justify-center align-baseline items-baseline">
                      <Link
                        href={`/${teamId}/students/${studentId}/goals/?addGoal=1`}
                        className="btn btn-primary btn-sm"
                      >
                        Create a goal
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Reports */}
        <div className="flex flex-col gap-8">
          {/* Heading */}
          <div className="flex justify-between -mb-4">
            <h2 className="text-xl font-bold">Reports</h2>
            <div className="flex gap-4 flex-row-reverse">
              {reportTypes.length > 0 && (
                <Dropdown
                  btn={
                    <Button className="btn btn-primary btn-sm">
                      New Report
                    </Button>
                  }
                  // If we close, then the form is unmounted and doesn't get submitted
                  noCloseOnClick
                >
                  <ul className="max-w-[60ch]">
                    {reportTypes.map((rt) => (
                      <li key={rt.id}>
                        <StudentReportCreatorButton
                          teamId={teamId}
                          studentId={studentId}
                          reportTypeId={rt.id}
                          reportTypeName={rt.name}
                          hasOtherReportsOfType={reports.some(
                            (report) => report.reportTypeId === rt.id,
                          )}
                        />
                      </li>
                    ))}
                  </ul>
                </Dropdown>
              )}
            </div>
          </div>
          {/* Reports by type */}
          {reportTypes
            .toSorted((a, b) => a.name.localeCompare(b.name))
            .map((reportType) => {
              const reportsOfType = reports.filter(
                (report) => report.reportTypeId === reportType.id,
              );
              if (reportsOfType.length === 0) {
                return null;
              }
              return (
                <div className="flex flex-col gap-4" key={reportType.id}>
                  <div className="flex justify-between">
                    <h2 className="text-lg font-bold opacity-70">
                      {reportType.name}
                    </h2>
                  </div>
                  {reports
                    .filter((report) => report.reportTypeId === reportType.id)
                    .map((report) => (
                      <Link
                        key={report.id}
                        href={`/${teamId}/students/${studentId}/reports/${report.id}`}
                        className="card bg-base-100  shadow"
                      >
                        <div className="card-body">
                          <div className="flex items-center">
                            {/* Report name and last updated date */}
                            <div className="flex flex-col gap-2">
                              <h3 className="card-title">{report.name}</h3>
                              <p className="text-sm text-gray-600">
                                {reportType.name} - Last updated{" "}
                                {report.updatedAt.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              );
            })}
          {/* If no reports, show an empty state that's full width and 5rem tall that says "No reports" */}
          {reports.length === 0 && (
            <div className="flex h-full min-h-[6rem] w-full place-content-center place-items-center italic opacity-70">
              No reports have been created yet.
            </div>
          )}
        </div>
        {/* Files */}
        <StudentFileList
          files={files}
          studentId={studentId}
          teamId={teamId}
        />
      </div>
    </WithStudentNav>
  );
}
